/**
 * Export branded a Excel del módulo de finanzas. Entrega mensual al
 * contador.
 *
 * Usa ExcelJS para tener colores, bordes, logo embebido y formato de
 * moneda. Bundle pesa ~600KB y se carga lazy solo cuando el usuario
 * hace click — no afecta el bundle inicial.
 *
 * Branding:
 *   - Banner morado con isotipo Axorial y título tipográfico
 *   - Banda cian de acento abajo del banner
 *   - Headers de tabla en morado con texto blanco
 *   - Filas zebra (gris muy claro alterno)
 *   - Money cells con formato MXN ($ + miles + 2 dec); negativos en rojo
 *   - Bordes sutiles en cuerpo de tablas
 *   - Freeze pane en la hoja Transacciones
 */
import type { Transaction } from "@axorial/shared";

type TxWithMeta = Transaction & {
  companyName: string | null;
};

interface ExportInput {
  transactions: TxWithMeta[];
  period: string | null;
  accountById: Map<string, string>;
  categoryById: Map<string, string>;
}

// ── Paleta de marca (ARGB; ExcelJS exige los 8 dígitos con alpha 'FF')
const BRAND = {
  purpleDeep: "FF2D1B4E",
  purple: "FF4A3A8C",
  purpleSoft: "FFEAE5F7",
  cyan: "FF2DB5C9",
  cyanSoft: "FFD8F1F5",
  orange: "FFE78A3C",
  stripe: "FFF6F4FB",
  border: "FFD8D3E3",
  white: "FFFFFFFF",
  text: "FF1F1431",
  textMuted: "FF6B6586",
  success: "FF159E5C",
  error: "FFC93030",
} as const;

// Custom number format: positivo → verde, negativo → rojo, cero → default.
// `[Green]` y `[Red]` son nombres reservados de Excel y se interpretan
// como colores fijos del workbook, independiente del tema.
const MONEY_FMT = '[Green]"$"#,##0.00;[Red]-"$"#,##0.00;"$"0.00';
const INT_FMT = "#,##0";

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  transfer: "Transferencia",
  card_credit: "Tarjeta crédito",
  card_debit: "Tarjeta débito",
  cash: "Efectivo",
};
const INVOICE_STATUS_LABEL: Record<string, string> = {
  facturado: "Facturado (CFDI)",
  deducible_sin_factura: "Deducible sin factura",
  pendiente: "Pendiente",
  no_aplica: "No aplica",
  olvidado: "Olvidado",
};
const PAYMENT_STATUS_LABEL: Record<string, string> = {
  cobrado: "Cobrado",
  pagado: "Pagado",
  pendiente: "Pendiente",
};
const KIND_LABEL: Record<string, string> = {
  ingreso: "Ingreso",
  gasto: "Gasto",
  transferencia: "Transferencia",
};

const fromCents = (cents: number) => Math.round(cents) / 100;

function periodLabel(period: string | null): string {
  if (!period) return "Sin periodo";
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  return new Date(y, m - 1, 1).toLocaleDateString("es-MX", {
    month: "long",
    year: "numeric",
  });
}

function nowLabel(): string {
  return new Date().toLocaleString("es-MX", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

async function loadLogoBuffer(): Promise<ArrayBuffer | null> {
  try {
    // Wordmark blanco calado — diseñado para fondos oscuros, fondo
    // transparente. Se coloca encima del banner morado.
    const res = await fetch("/logo/Logo-Axorial-Calado-Blanco.png");
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

export async function exportTransactionsToExcel(
  input: ExportInput,
  options?: { returnBuffer?: boolean },
): Promise<ArrayBuffer | null> {
  const returnBuffer = options?.returnBuffer === true;
  const ExcelJSMod = await import("exceljs");
  // exceljs export shape varía entre bundlers: a veces default, a veces el módulo.
  const ExcelJS = (ExcelJSMod as { default?: typeof import("exceljs") }).default ?? ExcelJSMod;
  const { transactions, period, accountById, categoryById } = input;

  const wb = new ExcelJS.Workbook();
  wb.creator = "Axorial Panel";
  wb.company = "Axorial Consultoría S.A. de C.V.";
  wb.created = new Date();
  wb.modified = new Date();

  const logoBuf = await loadLogoBuffer();
  const logoId =
    logoBuf !== null ? wb.addImage({ buffer: logoBuf as never, extension: "png" }) : null;

  // ── Totales del rango ───────────────────────────────────────────────
  let ingNeto = 0,
    ingIva = 0,
    gasNeto = 0,
    gasIva = 0,
    ingCobNeto = 0,
    ingCobIva = 0,
    gasPagNeto = 0,
    gasPagIva = 0;
  for (const t of transactions) {
    if (t.kind === "ingreso") {
      ingNeto += t.subtotalCents;
      ingIva += t.ivaCents;
      if (t.paymentStatus === "cobrado") {
        ingCobNeto += t.subtotalCents;
        ingCobIva += t.ivaCents;
      }
    } else if (t.kind === "gasto") {
      gasNeto += t.subtotalCents;
      gasIva += t.ivaCents;
      if (t.paymentStatus === "pagado") {
        gasPagNeto += t.subtotalCents;
        gasPagIva += t.ivaCents;
      }
    }
  }
  const baseIsr = Math.max(0, ingCobNeto - gasPagNeto);
  const isr = Math.round(baseIsr * 0.3);
  const ivaSaldo = ingCobIva - gasPagIva;
  const utilidad = ingNeto - gasNeto;

  // ╔══════════════════════════════════════════════════════════════════╗
  // ║ HOJA 1 — RESUMEN                                                ║
  // ╚══════════════════════════════════════════════════════════════════╝
  const resumen = wb.addWorksheet("Resumen", {
    views: [{ showGridLines: false }],
    properties: { defaultRowHeight: 18, tabColor: { argb: BRAND.purple } },
    pageSetup: {
      paperSize: 9, // A4
      orientation: "portrait",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
    },
    headerFooter: {
      oddFooter: "&L&8Axorial Panel&C&8Página &P de &N&R&8Generado " + nowLabel(),
    },
  });
  resumen.columns = [
    { width: 4 },
    { width: 42 },
    { width: 22 },
    { width: 22 },
    { width: 22 },
    { width: 4 },
  ];

  // Banner morado + logo (Resumen: 6 columnas usables)
  brandBanner(resumen, "Reporte fiscal · RESICO PM", periodLabel(period), logoId, 6);

  // Fila vacía antes del primer bloque
  let row = 7;
  resumen.getRow(row).height = 8;
  row += 1;

  // Sección: totales devengados
  row = sectionTitle(resumen, "Totales del rango (devengado)", row);
  row = totalsTable(
    resumen,
    [
      ["Ingresos", ingNeto, ingIva],
      ["Gastos", gasNeto, gasIva],
      ["Utilidad neta", utilidad, null],
    ],
    row,
  );
  row += 1;

  // Sección: flujo real
  row = sectionTitle(resumen, "Flujo real del rango (cobrado / pagado)", row);
  row = totalsTable(
    resumen,
    [
      ["Ingresos cobrados", ingCobNeto, ingCobIva],
      ["Gastos pagados", gasPagNeto, gasPagIva],
    ],
    row,
  );
  row += 1;

  // Sección: cálculo fiscal
  row = sectionTitle(resumen, "Cálculo fiscal — RESICO PM", row);
  row = fiscalTable(
    resumen,
    [
      ["Base ISR (ingresos cobrados − gastos pagados)", baseIsr],
      ["ISR a pagar (30% × base ISR)", isr],
      ["IVA trasladado (cobrado en ingresos)", ingCobIva],
      ["IVA acreditable (pagado en gastos)", gasPagIva],
      [
        `IVA saldo (${ivaSaldo > 0 ? "a pagar" : ivaSaldo < 0 ? "a favor" : "equilibrado"})`,
        ivaSaldo,
      ],
    ],
    row,
  );
  row += 1;

  // Sección: movimientos
  const cfdiOk = transactions.filter((t) => {
    if (t.kind === "transferencia") return false;
    if (t.invoiceStatus === "no_aplica" || t.invoiceStatus === "olvidado") return false;
    return t.invoiceUuid !== null || t.documentedExternally;
  }).length;
  const cfdiPend = transactions.filter((t) => {
    if (t.kind === "transferencia") return false;
    if (t.invoiceStatus === "no_aplica" || t.invoiceStatus === "olvidado") return false;
    return !t.invoiceUuid && !t.documentedExternally;
  }).length;

  row = sectionTitle(resumen, "Movimientos", row);
  row = countTable(
    resumen,
    [
      ["# transacciones", transactions.length],
      ["# con CFDI o declaradas", cfdiOk],
      ["# pendientes de CFDI", cfdiPend],
    ],
    row,
  );

  // Pie
  row += 2;
  const footCell = resumen.getCell(`B${row}`);
  footCell.value = `Generado por Axorial Panel · ${nowLabel()}`;
  footCell.font = { size: 9, italic: true, color: { argb: BRAND.textMuted } };
  resumen.mergeCells(`B${row}:E${row}`);

  // ╔══════════════════════════════════════════════════════════════════╗
  // ║ HOJA 2 — TRANSACCIONES                                          ║
  // ╚══════════════════════════════════════════════════════════════════╝
  const tx = wb.addWorksheet("Transacciones", {
    views: [{ showGridLines: false, state: "frozen", ySplit: 7 }],
    properties: { defaultRowHeight: 16, tabColor: { argb: BRAND.cyan } },
    pageSetup: {
      paperSize: 9, // A4
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      printTitlesRow: "7:7",
      margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
    },
    headerFooter: {
      oddFooter: "&L&8Axorial Panel · Transacciones&C&8Página &P de &N&R&8Generado " + nowLabel(),
    },
  });
  tx.columns = [
    { width: 3 }, // gutter izq
    { width: 12 }, // fecha op
    { width: 12 }, // fecha pago
    { width: 11 }, // tipo
    { width: 32 }, // empresa
    { width: 36 }, // concepto
    { width: 22 }, // categoría
    { width: 20 }, // cuenta
    { width: 16 }, // forma de pago
    { width: 14 }, // subtotal
    { width: 12 }, // iva
    { width: 14 }, // total
    { width: 22 }, // estado cfdi
    { width: 12 }, // estado pago
    { width: 36 }, // uuid
    { width: 14 }, // declarado ext
    { width: 10 }, // aplica isr
    { width: 36 }, // notas
    { width: 3 }, // gutter der
  ];
  brandBanner(tx, "Transacciones del periodo", periodLabel(period), logoId, 19);

  const txHeaderRow = 7;
  const txHeader = [
    "",
    "Fecha op.",
    "Fecha pago",
    "Tipo",
    "Empresa / contraparte",
    "Concepto",
    "Categoría",
    "Cuenta",
    "Forma de pago",
    "Subtotal",
    "IVA",
    "Total",
    "Estado CFDI",
    "Estado pago",
    "UUID CFDI",
    "Declarado ext.",
    "Aplica ISR",
    "Notas",
  ];
  txHeader.forEach((label, idx) => {
    const cell = tx.getCell(txHeaderRow, idx + 1);
    cell.value = label;
    if (idx === 0) return;
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND.purple } };
    cell.font = { bold: true, color: { argb: BRAND.white }, size: 11 };
    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: false };
    cell.border = {
      top: { style: "thin", color: { argb: BRAND.purple } },
      bottom: { style: "medium", color: { argb: BRAND.cyan } },
    };
  });
  tx.getRow(txHeaderRow).height = 24;

  const sorted = [...transactions].toSorted((a, b) =>
    (a.paymentDate ?? a.date).localeCompare(b.paymentDate ?? b.date),
  );

  sorted.forEach((t, i) => {
    const r = txHeaderRow + 1 + i;
    const isStripe = i % 2 === 1;
    const stripeFill = isStripe
      ? { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: BRAND.stripe } }
      : null;

    // Signo: gastos van negativos (rojo), ingresos positivos (verde),
    // transferencias se quedan en su monto sin signo — el formato de
    // moneda con `[Red]-"$"#,##0.00` los pinta automáticamente. Esto
    // permite que las fórmulas SUMIF de abajo den el neto sin más
    // cálculos.
    const sign = t.kind === "gasto" ? -1 : 1;
    const values: (string | number | Date | null)[] = [
      null, // gutter
      t.date,
      t.paymentDate ?? "",
      KIND_LABEL[t.kind] ?? t.kind,
      t.companyName ?? t.counterparty ?? "",
      t.title?.trim() || t.description?.trim() || "",
      t.categoryId ? (categoryById.get(t.categoryId) ?? "") : "",
      accountById.get(t.accountId) ?? t.accountId,
      t.paymentMethod ? (PAYMENT_METHOD_LABEL[t.paymentMethod] ?? t.paymentMethod) : "",
      sign * fromCents(t.subtotalCents),
      sign * fromCents(t.ivaCents),
      sign * fromCents(t.totalCents),
      INVOICE_STATUS_LABEL[t.invoiceStatus] ?? t.invoiceStatus,
      PAYMENT_STATUS_LABEL[t.paymentStatus] ?? t.paymentStatus,
      t.invoiceUuid ?? "",
      t.documentedExternally ? "Sí" : "",
      t.isrApplicable ? "Sí" : "No",
      t.description?.trim() ?? "",
    ];

    values.forEach((v, colIdx) => {
      const cell = tx.getCell(r, colIdx + 1);
      cell.value = v;
      if (colIdx === 0) return;
      if (stripeFill) cell.fill = stripeFill;
      cell.font = {
        size: 10,
        color: { argb: BRAND.text },
      };
      cell.alignment = { vertical: "middle", horizontal: "left", wrapText: false };
      cell.border = {
        bottom: { style: "thin", color: { argb: BRAND.border } },
      };
    });

    // Tinte de la columna "Tipo" según ingreso/gasto/transferencia
    const tipoCell = tx.getCell(r, 4);
    if (t.kind === "ingreso") {
      tipoCell.font = { size: 10, bold: true, color: { argb: BRAND.success } };
    } else if (t.kind === "gasto") {
      tipoCell.font = { size: 10, bold: true, color: { argb: BRAND.error } };
    } else {
      tipoCell.font = { size: 10, bold: true, color: { argb: BRAND.cyan } };
    }

    // Money cells con formato MXN
    [10, 11, 12].forEach((col) => {
      const c = tx.getCell(r, col);
      c.numFmt = MONEY_FMT;
      c.alignment = { vertical: "middle", horizontal: "right" };
    });
  });

  // Totales abajo — fórmulas en vivo. Excluyen transferencias usando
  // SUMIF sobre la columna Tipo (D). Como los gastos ya están guardados
  // como negativos, basta sumar Ingreso + Gasto: el neto sale solo.
  if (sorted.length > 0) {
    const totalRow = txHeaderRow + sorted.length + 1;
    const firstData = txHeaderRow + 1;
    const lastData = txHeaderRow + sorted.length;

    tx.getCell(totalRow, 5).value = "TOTALES (ingreso − gasto)";
    tx.getCell(totalRow, 5).font = { bold: true, color: { argb: BRAND.purple } };
    tx.getCell(totalRow, 5).alignment = { horizontal: "right" };
    tx.getCell(totalRow, 5).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: BRAND.purpleSoft },
    };
    tx.getCell(totalRow, 5).border = {
      top: { style: "medium", color: { argb: BRAND.purple } },
    };

    // Helper para construir la fórmula SUMIF por columna numérica.
    const sumifFormula = (colLetter: string) =>
      `SUMIF($D$${firstData}:$D$${lastData},"Ingreso",${colLetter}${firstData}:${colLetter}${lastData})` +
      `+SUMIF($D$${firstData}:$D$${lastData},"Gasto",${colLetter}${firstData}:${colLetter}${lastData})`;

    (
      [
        [10, "J"],
        [11, "K"],
        [12, "L"],
      ] as const
    ).forEach(([col, letter]) => {
      const c = tx.getCell(totalRow, col);
      c.value = { formula: sumifFormula(letter) };
      c.numFmt = MONEY_FMT;
      c.font = { bold: true, color: { argb: BRAND.purple } };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND.purpleSoft } };
      c.border = { top: { style: "medium", color: { argb: BRAND.purple } } };
      c.alignment = { horizontal: "right" };
    });
  }

  // Auto-filtro en el header
  tx.autoFilter = { from: { row: txHeaderRow, column: 2 }, to: { row: txHeaderRow, column: 18 } };

  // ╔══════════════════════════════════════════════════════════════════╗
  // ║ HOJA 3 — POR CATEGORÍA                                          ║
  // ╚══════════════════════════════════════════════════════════════════╝
  const cat = wb.addWorksheet("Por categoría", {
    views: [{ showGridLines: false, state: "frozen", ySplit: 7 }],
    properties: { defaultRowHeight: 16, tabColor: { argb: BRAND.orange } },
    pageSetup: {
      paperSize: 9, // A4
      orientation: "portrait",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      printTitlesRow: "7:7",
      margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
    },
    headerFooter: {
      oddFooter: "&L&8Axorial Panel · Por categoría&C&8Página &P de &N&R&8Generado " + nowLabel(),
    },
  });
  cat.columns = [
    { width: 3 },
    { width: 32 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 3 },
  ];
  brandBanner(cat, "Agregado por categoría", periodLabel(period), logoId, 7);

  const catHeader = [
    "",
    "Categoría",
    "Ingresos (neto)",
    "IVA trasladado",
    "Gastos (neto)",
    "IVA acreditable",
  ];
  const catHeaderRow = 7;
  catHeader.forEach((label, idx) => {
    const c = cat.getCell(catHeaderRow, idx + 1);
    c.value = label;
    if (idx === 0) return;
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND.purple } };
    c.font = { bold: true, color: { argb: BRAND.white }, size: 11 };
    c.alignment = { vertical: "middle", horizontal: idx === 1 ? "left" : "right" };
    c.border = {
      top: { style: "thin", color: { argb: BRAND.purple } },
      bottom: { style: "medium", color: { argb: BRAND.cyan } },
    };
  });
  cat.getRow(catHeaderRow).height = 24;

  const byCat = new Map<
    string,
    { ingreso: number; ingresoIva: number; gasto: number; gastoIva: number }
  >();
  for (const t of transactions) {
    if (t.kind === "transferencia") continue;
    const name = t.categoryId ? (categoryById.get(t.categoryId) ?? "—") : "Sin categoría";
    let b = byCat.get(name);
    if (!b) {
      b = { ingreso: 0, ingresoIva: 0, gasto: 0, gastoIva: 0 };
      byCat.set(name, b);
    }
    if (t.kind === "ingreso") {
      b.ingreso += t.subtotalCents;
      b.ingresoIva += t.ivaCents;
    } else {
      b.gasto += t.subtotalCents;
      b.gastoIva += t.ivaCents;
    }
  }
  const catRows = [...byCat.entries()].toSorted((a, b) => a[0].localeCompare(b[0]));
  catRows.forEach(([name, v], i) => {
    const r = catHeaderRow + 1 + i;
    const stripe = i % 2 === 1;
    const fill = stripe
      ? { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: BRAND.stripe } }
      : null;
    const values: (string | number)[] = [
      "",
      name,
      fromCents(v.ingreso),
      fromCents(v.ingresoIva),
      fromCents(v.gasto),
      fromCents(v.gastoIva),
    ];
    values.forEach((val, idx) => {
      const c = cat.getCell(r, idx + 1);
      c.value = val;
      if (idx === 0) return;
      if (fill) c.fill = fill;
      c.font = { size: 10, color: { argb: BRAND.text } };
      c.alignment = { vertical: "middle", horizontal: idx === 1 ? "left" : "right" };
      c.border = { bottom: { style: "thin", color: { argb: BRAND.border } } };
      if (idx >= 2) c.numFmt = MONEY_FMT;
    });
  });

  // Totales de categoría
  const catTotalRow = catHeaderRow + 1 + catRows.length;
  cat.getCell(catTotalRow, 2).value = "TOTAL";
  cat.getCell(catTotalRow, 2).font = { bold: true, color: { argb: BRAND.purple } };
  cat.getCell(catTotalRow, 2).alignment = { horizontal: "right" };
  const totalIng = catRows.reduce((s, [, v]) => s + v.ingreso, 0);
  const totalIngIva = catRows.reduce((s, [, v]) => s + v.ingresoIva, 0);
  const totalGas = catRows.reduce((s, [, v]) => s + v.gasto, 0);
  const totalGasIva = catRows.reduce((s, [, v]) => s + v.gastoIva, 0);
  [
    [3, totalIng],
    [4, totalIngIva],
    [5, totalGas],
    [6, totalGasIva],
  ].forEach(([col, val]) => {
    const c = cat.getCell(catTotalRow, col!);
    c.value = fromCents(val!);
    c.numFmt = MONEY_FMT;
    c.font = { bold: true, color: { argb: BRAND.purple } };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND.purpleSoft } };
    c.border = { top: { style: "medium", color: { argb: BRAND.purple } } };
    c.alignment = { horizontal: "right" };
  });

  // ── Descarga / buffer ───────────────────────────────────────────────
  const buf = await wb.xlsx.writeBuffer();
  if (returnBuffer) {
    void INT_FMT;
    return buf as ArrayBuffer;
  }
  const blob = new Blob([buf as BlobPart], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = period
    ? `axorial-finanzas-${period}.xlsx`
    : `axorial-finanzas-${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  void INT_FMT;
  return null;
}

// ─── Helpers de branding ────────────────────────────────────────────────

type WS = import("exceljs").Worksheet;

/**
 * Pinta el banner morado superior con el wordmark Axorial blanco + meta.
 * Ocupa las filas 1-6 a lo ancho de las primeras `lastCol` columnas.
 *
 * Layout:
 *   - Filas 1-4: bloque morado profundo (28pt cada una = ~112pt total)
 *   - Logo wordmark "axorial" en blanco/cyan, anclado top-left (~300px×100px)
 *   - Subtítulo (título de hoja · periodo) alineado a la derecha en cyan
 *   - Fila 5: banda cyan de acento
 *   - Fila 6: separador en blanco
 */
function brandBanner(
  ws: WS,
  title: string,
  period: string,
  logoId: number | null,
  lastCol: number,
): void {
  // Filas 1-4: bloque morado profundo a todo lo ancho usable
  for (let r = 1; r <= 4; r++) {
    ws.getRow(r).height = 28;
    for (let c = 1; c <= lastCol; c++) {
      ws.getCell(r, c).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: BRAND.purpleDeep },
      };
    }
  }

  // Wordmark — ocupa el área izquierda del banner. El PNG fuente tiene
  // padding interno generoso, así que con 300x100 el wordmark se ve
  // proporcionado dentro del bloque morado.
  if (logoId !== null) {
    ws.addImage(logoId, {
      tl: { col: 0.2, row: 0.2 },
      ext: { width: 300, height: 110 },
    });
  }

  // Subtítulo alineado a la derecha: título de hoja + periodo, en cyan
  // soft itálica. Ocupa las últimas columnas del banner.
  const subStartCol = Math.max(3, lastCol - 5);
  const subCell = ws.getCell(3, subStartCol);
  subCell.value = `${title}  ·  ${period}`;
  subCell.font = {
    name: "Calibri",
    size: 13,
    color: { argb: BRAND.cyanSoft },
    italic: true,
  };
  subCell.alignment = { vertical: "middle", horizontal: "right", indent: 1 };
  ws.mergeCells(3, subStartCol, 3, lastCol);

  // Banda cyan completa (fila 5) y separador en blanco (fila 6)
  ws.getRow(5).height = 6;
  for (let c = 1; c <= lastCol; c++) {
    ws.getCell(5, c).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: BRAND.cyan },
    };
  }
  ws.getRow(6).height = 4;
}

/** Título de sección en morado, ocupa toda la fila usable (B:E). */
function sectionTitle(ws: WS, label: string, row: number): number {
  const c = ws.getCell(`B${row}`);
  c.value = label.toUpperCase();
  c.font = {
    name: "Calibri",
    size: 11,
    bold: true,
    color: { argb: BRAND.purple },
  };
  c.alignment = { vertical: "middle", horizontal: "left" };
  ws.mergeCells(`B${row}:E${row}`);
  ws.getRow(row).height = 22;
  // Línea cian debajo
  for (let col = 2; col <= 5; col++) {
    ws.getCell(row, col).border = {
      bottom: { style: "medium", color: { argb: BRAND.cyan } },
    };
  }
  return row + 1;
}

/**
 * Tabla de 4 columnas: concepto + neto + IVA + total. IVA puede ser
 * null para filas que no tienen desglose IVA.
 */
function totalsTable(ws: WS, rows: [string, number, number | null][], start: number): number {
  // Header
  const headers = ["Concepto", "Neto", "IVA", "Total"];
  headers.forEach((h, i) => {
    const c = ws.getCell(start, i + 2);
    c.value = h;
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND.purple } };
    c.font = { bold: true, color: { argb: BRAND.white }, size: 10 };
    c.alignment = { vertical: "middle", horizontal: i === 0 ? "left" : "right" };
    c.border = { bottom: { style: "thin", color: { argb: BRAND.cyan } } };
  });
  ws.getRow(start).height = 20;

  rows.forEach(([label, neto, iva], i) => {
    const r = start + 1 + i;
    const stripe = i % 2 === 1;
    const fill = stripe
      ? { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: BRAND.stripe } }
      : null;
    const total = iva === null ? null : neto + iva;
    const cells: (string | number | null)[] = [
      label,
      fromCents(neto),
      iva === null ? null : fromCents(iva),
      total === null ? null : fromCents(total),
    ];
    cells.forEach((v, ci) => {
      const c = ws.getCell(r, ci + 2);
      c.value = v;
      if (fill) c.fill = fill;
      c.font = { size: 10, color: { argb: BRAND.text } };
      c.alignment = { vertical: "middle", horizontal: ci === 0 ? "left" : "right" };
      c.border = { bottom: { style: "thin", color: { argb: BRAND.border } } };
      if (ci >= 1 && v !== null) c.numFmt = MONEY_FMT;
    });
  });
  return start + 1 + rows.length;
}

/** Tabla de 2 columnas: concepto + monto. */
function fiscalTable(ws: WS, rows: [string, number][], start: number): number {
  const headers = ["Concepto", "Monto MXN"];
  headers.forEach((h, i) => {
    const col = i === 0 ? 2 : 5;
    const c = ws.getCell(start, col);
    c.value = h;
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND.purple } };
    c.font = { bold: true, color: { argb: BRAND.white }, size: 10 };
    c.alignment = { vertical: "middle", horizontal: i === 0 ? "left" : "right" };
    c.border = { bottom: { style: "thin", color: { argb: BRAND.cyan } } };
  });
  ws.mergeCells(`B${start}:D${start}`);
  ws.getRow(start).height = 20;

  rows.forEach(([label, amt], i) => {
    const r = start + 1 + i;
    const stripe = i % 2 === 1;
    const fill = stripe
      ? { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: BRAND.stripe } }
      : null;
    // Etiqueta merge B:D
    const labelCell = ws.getCell(`B${r}`);
    labelCell.value = label;
    if (fill) labelCell.fill = fill;
    labelCell.font = { size: 10, color: { argb: BRAND.text } };
    labelCell.alignment = { vertical: "middle", horizontal: "left" };
    labelCell.border = { bottom: { style: "thin", color: { argb: BRAND.border } } };
    ws.mergeCells(`B${r}:D${r}`);
    // También tintar las celdas mergeadas (Excel a veces las renderiza separadas)
    for (let col = 3; col <= 4; col++) {
      const c = ws.getCell(r, col);
      if (fill) c.fill = fill;
      c.border = { bottom: { style: "thin", color: { argb: BRAND.border } } };
    }
    // Monto en E
    const amtCell = ws.getCell(`E${r}`);
    amtCell.value = fromCents(amt);
    amtCell.numFmt = MONEY_FMT;
    if (fill) amtCell.fill = fill;
    amtCell.font = { size: 10, bold: true, color: { argb: BRAND.text } };
    amtCell.alignment = { vertical: "middle", horizontal: "right" };
    amtCell.border = { bottom: { style: "thin", color: { argb: BRAND.border } } };
  });
  return start + 1 + rows.length;
}

/** Tabla de conteo: concepto + cantidad entera. */
function countTable(ws: WS, rows: [string, number][], start: number): number {
  rows.forEach(([label, n], i) => {
    const r = start + i;
    const stripe = i % 2 === 1;
    const fill = stripe
      ? { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: BRAND.stripe } }
      : null;
    const labelCell = ws.getCell(`B${r}`);
    labelCell.value = label;
    if (fill) labelCell.fill = fill;
    labelCell.font = { size: 10, color: { argb: BRAND.text } };
    labelCell.alignment = { vertical: "middle", horizontal: "left" };
    labelCell.border = { bottom: { style: "thin", color: { argb: BRAND.border } } };
    ws.mergeCells(`B${r}:D${r}`);
    for (let col = 3; col <= 4; col++) {
      const c = ws.getCell(r, col);
      if (fill) c.fill = fill;
      c.border = { bottom: { style: "thin", color: { argb: BRAND.border } } };
    }
    const amtCell = ws.getCell(`E${r}`);
    amtCell.value = n;
    amtCell.numFmt = "#,##0";
    if (fill) amtCell.fill = fill;
    amtCell.font = { size: 10, bold: true, color: { argb: BRAND.text } };
    amtCell.alignment = { vertical: "middle", horizontal: "right" };
    amtCell.border = { bottom: { style: "thin", color: { argb: BRAND.border } } };
  });
  return start + rows.length;
}
